import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { datePadStart, dotFormatter, getFrequencyEndDate } from '../../../../../helpers';
import { useTranslation } from '../../../../../hooks';
import { CashbackInfo } from '../../../../../models';
import { RewardsTimerDigit } from './timer-digit';
import { T } from '../../../../../constants';

interface RewardsDetailedPopupTimerOrBudgetProps {
  currencyCode: string;
  showBudget?: boolean;
  data: CashbackInfo;
}

const initialTimer = { days: 0, hours: 0, minutes: 0, seconds: 0 };

export const RewardsDetailedPopupTimerOrBudget: React.FC<RewardsDetailedPopupTimerOrBudgetProps> = ({
  data,
  currencyCode,
  showBudget = false,
}) => {

  const t = useTranslation();

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const [timer, setTimer] = useState(initialTimer);

  const { budgetAmount, remainingBudgetAmount, claimingFrequency, endDate } = data;

  const hasBudget = !!budgetAmount;
  const hasFrequency = claimingFrequency || claimingFrequency === 0;

  const title = hasFrequency ? T.RESET_IN : T.TIME_LEFT;

  const clearTimerInterval = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
  }, []);

  const countdownDate = useMemo(() => {
    return hasFrequency ? getFrequencyEndDate(claimingFrequency) : new Date(endDate);
  }, [hasFrequency, claimingFrequency, endDate]);

  const updateTimer = useCallback(() => {
    const now = Date.now();
    const end = countdownDate.getTime();

    if (end <= now) {
      setTimer({ ...initialTimer });
      clearTimerInterval();
      return;
    }

    const duration = end - now;
    setTimer({
      days: Math.floor(duration / (1000 * 60 * 60 * 24)),
      hours: Math.floor((duration / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((duration / 1000 / 60) % 60),
      seconds: Math.floor((duration / 1000) % 60),
    });

    if (duration / 1000 < 1) {
      clearTimerInterval();
    }
  }, [countdownDate, clearTimerInterval]);
  
  useEffect(() => {
    updateTimer();
    intervalIdRef.current = setInterval(updateTimer, 900);
    return () => clearTimerInterval();
  }, []);

  return (
    <div className="time-wrapper">
      <p className="rewards-time-title">
        {t(title)}
      </p>
      <div className="rewards-time-cont">
        <div className="rewards-timer-wr">
          <RewardsTimerDigit
            value={datePadStart(timer.days)}
            label={T.DAYS_TOURNAMENT}
          />
          <RewardsTimerDigit
            value={datePadStart(timer.hours)}
            label={T.HRS_TOURNAMENT}
          />
          <RewardsTimerDigit
            value={datePadStart(timer.minutes)}
            label={T.MINS_TOURNAMENT}
          />
          <RewardsTimerDigit
            hideDots
            value={datePadStart(timer.seconds)}
            label={T.SECS_TOURNAMENT}
          />
        </div>
        {
          (showBudget && hasBudget) && (
            <>
              <p className="rewards-time-title margin-top">
                {t(T.BUDGET_LEFT)}
              </p>
              <p className="rewards-time-box">
                <span className="rewards-time-num">
                  {dotFormatter(remainingBudgetAmount || 0)} / {dotFormatter(budgetAmount || 0)} {currencyCode}
                </span>
              </p >
            </>
          )
        }
      </div >
    </div >
  );
};
